import { type DragEvent } from "react";
import { ArrowDownToLine, Copy, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { composerBus } from "@/lib/composerBus";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import {
  BLOCK_TYPE_DESCRIPTIONS,
  BLOCK_TYPE_LABELS,
  composeBlockDocFragment,
  composeBlockDropFragments,
  composeBlockPlainText,
  type ComposerBlock,
  type ComposerBlockType,
} from "@/lib/composerBlocks";

const handleDragStart = (block: ComposerBlock) => (e: DragEvent<HTMLDivElement>) => {
  e.dataTransfer.setData("text/plain", composeBlockPlainText(block));
  // `doc` retained for back-compat with any consumers reading the legacy
  // shape. New consumers should read `fragments`.
  const doc = composeBlockDocFragment(block);
  const fragments = composeBlockDropFragments(block);
  e.dataTransfer.setData(
    "application/x-excerptus-block",
    JSON.stringify({
      id: block.id,
      type: block.type,
      title: block.title,
      body: block.body,
      signatureName: block.signatureName ?? null,
      doc,
      fragments,
    }),
  );
  e.dataTransfer.effectAllowed = "copy";
};

type Props = {
  mode: "catalog" | "drag";
  categoryOrder: ComposerBlockType[];
  grouped: Map<ComposerBlockType, ComposerBlock[]>;
  usageCounts: Map<string, number> | undefined;
  dragType: ComposerBlockType | null;
  setDragType: (v: ComposerBlockType | null) => void;
  dragOverType: ComposerBlockType | null;
  setDragOverType: (v: ComposerBlockType | null) => void;
  onCategoryDrop: (target: ComposerBlockType) => Promise<void>;
  openEdit: (block: ComposerBlock) => void;
  openNew: (type?: ComposerBlockType) => void;
  setConfirmDelete: (block: ComposerBlock) => void;
};

export function BlockCategoryAccordion(p: Props) {
  return (
    <Accordion type="multiple" defaultValue={[]} className="flex-1 overflow-auto">
      {p.categoryOrder.map((type) => {
        const items = p.grouped.get(type) ?? [];
        const isOver = p.dragOverType === type && p.dragType && p.dragType !== type;
        return (
          <AccordionItem
            key={type}
            value={type}
            onDragOver={(e) => {
              if (!p.dragType) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (p.dragOverType !== type) p.setDragOverType(type);
            }}
            onDragLeave={() => {
              if (p.dragOverType === type) p.setDragOverType(null);
            }}
            onDrop={(e) => {
              if (!p.dragType) return;
              e.preventDefault();
              void p.onCategoryDrop(type);
            }}
            className={isOver ? "border-t-2 border-primary" : undefined}
          >
            <AccordionTrigger className="py-2 text-sm">
              <div className="flex w-full items-center gap-2 pr-2">
                <span
                  role="button"
                  aria-label={`Reorder ${BLOCK_TYPE_LABELS[type]}`}
                  title="Drag to reorder category"
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("application/x-excerptus-block-category", type);
                    p.setDragType(type);
                  }}
                  onDragEnd={() => { p.setDragType(null); p.setDragOverType(null); }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="-ml-1 inline-flex h-5 w-5 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground/60 hover:text-foreground active:cursor-grabbing"
                >
                  <GripVertical className="h-3.5 w-3.5" />
                </span>
                <span className="font-medium">{BLOCK_TYPE_LABELS[type]}</span>
                <Badge variant="outline" className="text-[10px] tabular-nums">
                  {items.length}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {p.mode === "catalog" && (
                <p className="mb-2 text-xs text-muted-foreground">
                  {BLOCK_TYPE_DESCRIPTIONS[type]}
                </p>
              )}
              {items.length === 0 ? (
                <p className="rounded-md border border-dashed border-border/60 p-3 text-xs text-muted-foreground">
                  No blocks yet.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {items.map((block) => (
                    <li key={block.id}>
                      <div
                        draggable={p.mode === "drag"}
                        onDragStart={p.mode === "drag" ? handleDragStart(block) : undefined}
                        className={
                          "group rounded-md border border-border/60 bg-card p-2 text-left transition-colors " +
                          (p.mode === "drag"
                            ? "cursor-grab hover:border-primary/60 hover:bg-accent/40 active:cursor-grabbing"
                            : "")
                        }
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <p className="truncate text-xs font-medium">{block.title}</p>
                            {(() => {
                              const n = p.usageCounts?.get(block.id) ?? 0;
                              return n > 0 ? (
                                <Badge
                                  variant="secondary"
                                  className="h-4 shrink-0 px-1 text-[9px] tabular-nums"
                                  title={`Used in ${n} blueprint${n === 1 ? "" : "s"}`}
                                >
                                  {n}
                                </Badge>
                              ) : null;
                            })()}
                          </div>
                          <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            {p.mode === "drag" && (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => {
                                    composerBus.emit("draft:insert", {
                                      doc: composeBlockDocFragment(block) as { type: "doc"; content: unknown[] },
                                      mode: "append-at-cursor",
                                      source: `block:${block.id}`,
                                      label: block.title,
                                    });
                                  }}
                                  aria-label="Insert at cursor"
                                  title="Insert at cursor in Draft Canvas"
                                >
                                  <ArrowDownToLine className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(block.body);
                                      toast({ title: "Copied to clipboard" });
                                    } catch {
                                      toast({ title: "Copy failed", variant: "destructive" });
                                    }
                                  }}
                                  aria-label="Copy block"
                                  title="Copy block text"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                            {p.mode === "catalog" && (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => p.openEdit(block)}
                                  aria-label="Edit block"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 text-destructive hover:text-destructive"
                                  onClick={() => p.setConfirmDelete(block)}
                                  aria-label="Delete block"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="mt-1 flex items-start gap-2">
                          {(block.type === "signature" || block.type === "author_bio" || block.type === "product_cta") && block.imageUrl && (
                            <img
                              src={block.imageUrl}
                              alt={block.imageAlt || "Block image"}
                              className="h-10 w-10 shrink-0 rounded border border-border/60 bg-background object-contain"
                            />
                          )}

                          <p className="line-clamp-2 whitespace-pre-line text-[11px] text-muted-foreground">
                            {block.body}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {p.mode === "catalog" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-2 text-xs"
                  onClick={() => p.openNew(type)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  New {BLOCK_TYPE_LABELS[type].toLowerCase()}
                </Button>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
